// Settings > Patient portal (docs/plans/phase3-portal-feedback-kiswahili.md §2.1, §7.2):
// switch the portal on, choose its public code, set each facility's paybill, and add the
// consent wording read to patients (TODO(legal-verify): Wodi ships no approved wording).
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import type { PortalSettingsView } from '@wodi/shared';
import {
  Button,
  Checkbox,
  LoadingBlock,
  Notice,
  PageHeader,
  RadioGroup,
  Section,
  TextArea,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import {
  addPortalConsentText,
  adminPortalConsentTextsQuery,
  portalSettingsQuery,
  portalStaffKeys,
  saveFacilityPaybill,
  savePortalSettings,
} from '../api/portal-staff.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { screen } from '../layout/screen.tsx';
import { toasts } from '../toasts.ts';

const message = (error: unknown) =>
  error instanceof ApiError ? error.message : 'Something went wrong. Try again.';

function PortalForm({ settings }: { settings: PortalSettingsView }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(settings.enabled);
  const [code, setCode] = useState(settings.portalCode ?? '');
  const save = useMutation({
    mutationFn: () => savePortalSettings({ enabled, portalCode: code.trim().toLowerCase() }),
    onSuccess: (saved) => {
      queryClient.setQueryData(portalStaffKeys.settings, saved);
      toasts.show({ title: 'Patient portal saved' });
    },
  });
  return (
    <Section
      title="Portal"
      description="Patients sign in with their mobile number and an SMS code."
    >
      <form
        className={screen.page}
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        {save.error === null ? null : <Notice tone="error">{message(save.error)}</Notice>}
        <Checkbox isSelected={enabled} onChange={setEnabled}>
          Patients can sign in to the portal
        </Checkbox>
        <TextField
          label="Portal code"
          description="Lower-case letters, numbers and dashes. It is in the portal address patients open, so it is public."
          value={code}
          onChange={setCode}
          width="md"
        />
        {code.trim() === '' ? null : (
          <p>{`Address: ${window.location.origin}/portal/${code.trim().toLowerCase()}`}</p>
        )}
        <div>
          <Button type="submit" variant="primary" isDisabled={!online} isPending={save.isPending}>
            Save
          </Button>
        </div>
      </form>
    </Section>
  );
}

function Paybills({ settings }: { settings: PortalSettingsView }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const queryClient = useQueryClient();
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(settings.facilities.map((f) => [f.id, f.paybillNumber ?? ''])),
  );
  const save = useMutation({
    mutationFn: (facilityId: string) => {
      const value = (values[facilityId] ?? '').trim();
      return saveFacilityPaybill(facilityId, value === '' ? null : value);
    },
    onSuccess: (saved) => {
      queryClient.setQueryData(portalStaffKeys.settings, saved);
      toasts.show({ title: 'Paybill saved' });
    },
  });
  const mayEdit = session !== null && can(session, 'admin.facilities.manage');
  return (
    <Section
      title="How patients pay"
      description="The facility's own M-Pesa paybill. The portal shows it with the visit number as the account; Wodi never takes money."
    >
      {save.error === null ? null : <Notice tone="error">{message(save.error)}</Notice>}
      {settings.facilities.map((f) => (
        <form
          key={f.id}
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate(f.id);
          }}
        >
          <TextField
            label={`${f.name}: paybill number`}
            value={values[f.id] ?? ''}
            onChange={(v) => {
              setValues((all) => ({ ...all, [f.id]: v }));
            }}
            isDisabled={!mayEdit}
            width="sm"
          />
          {mayEdit ? (
            <Button
              dense
              type="submit"
              isDisabled={!online}
              isPending={save.isPending && save.variables === f.id}
            >
              Save paybill
            </Button>
          ) : null}
        </form>
      ))}
    </Section>
  );
}

function ConsentWording() {
  const online = useOnline();
  const queryClient = useQueryClient();
  const texts = useQuery(adminPortalConsentTextsQuery);
  const [language, setLanguage] = useState<'en' | 'sw'>('en');
  const [version, setVersion] = useState('');
  const [body, setBody] = useState('');
  const add = useMutation({
    mutationFn: () => addPortalConsentText({ version, language, body }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: portalStaffKeys.adminConsentTexts });
      setVersion('');
      setBody('');
      toasts.show({ title: 'Consent wording added' });
    },
  });
  return (
    <Section
      title="Consent wording"
      description="What reception reads to a patient before giving portal access. A new version replaces the one in use for its language."
    >
      {texts.isPending ? <LoadingBlock label="Loading consent wording" rows={2} /> : null}
      {texts.isError ? <Notice tone="error">{message(texts.error)}</Notice> : null}
      {texts.data?.length === 0 ? (
        <Notice tone="info">
          No wording yet. Patients can't be given portal access until it is added.
        </Notice>
      ) : null}
      <ul>
        {(texts.data ?? []).map((t) => (
          <li key={t.id}>
            {`${t.language === 'sw' ? 'Kiswahili' : 'English'}, version ${t.version}${t.status === 'retired' ? ' (replaced)' : ' (in use)'}: ${t.body}`}
          </li>
        ))}
      </ul>
      <form
        className={screen.page}
        onSubmit={(event) => {
          event.preventDefault();
          add.mutate();
        }}
      >
        {add.error === null ? null : <Notice tone="error">{message(add.error)}</Notice>}
        <RadioGroup
          label="Language"
          orientation="horizontal"
          options={[
            { id: 'en', label: 'English' },
            { id: 'sw', label: 'Kiswahili' },
          ]}
          value={language}
          onChange={(v) => {
            setLanguage(v === 'sw' ? 'sw' : 'en');
          }}
        />
        <TextField label="Version" value={version} onChange={setVersion} width="sm" />
        <TextArea label="Wording" rows={5} value={body} onChange={setBody} />
        <div>
          <Button
            type="submit"
            isDisabled={!online || version.trim() === '' || body.trim() === ''}
            isPending={add.isPending}
          >
            Add wording
          </Button>
        </div>
      </form>
    </Section>
  );
}

export function PortalSettingsPage() {
  const online = useOnline();
  const settings = useQuery(portalSettingsQuery);
  return (
    <div className={screen.page}>
      <PageHeader title="Patient portal" />
      {settings.isPending ? <LoadingBlock label="Loading portal settings" rows={3} /> : null}
      {settings.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense isDisabled={!online} onPress={() => void settings.refetch()}>
              Try again
            </Button>
          }
        >
          {online ? message(settings.error) : "You're offline. Settings load once you reconnect."}
        </Notice>
      ) : null}
      {settings.data === undefined ? null : (
        <>
          <PortalForm settings={settings.data} />
          <Paybills settings={settings.data} />
          <ConsentWording />
        </>
      )}
    </div>
  );
}
